import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { connect } from "react-redux";

import { Location as LocationModel } from "lib/models/Location";

import SimpleTable from "views/Components/SimpleTable";
import SimpleModelForm from "views/Components/SimpleModelForm";
import EditIcon from "@material-ui/icons/Edit";
import AddCircleIcon from "@material-ui/icons/AddCircle";

import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";

import countries from "i18n-iso-countries";

import { loadPageData } from "redux/actions/Request";

// https://stackoverflow.com/questions/58136474/how-to-use-i18n-iso-countries-in-react
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const LocationsPageView = ({
  ready,
  locations = [],
  timezones = [],
  reload,
  orgId,
}) => {
  const [selectedModel, setSelectedModel] = useState(false);
  const [openForm, setOpenForm] = useState(false);

  const handleClose = (action) => {
    selectedModel.reset();
    if (action === "create") {
      reload(true);
    }
    setOpenForm(false);
  };

  const getUserTimezone = () => {
    let userTz = null;

    try {
      const payload = JSON.parse(localStorage.getItem("idTokenPayload"));
      userTz = payload["https://b5a.io/geoip"].time_zone;
    } catch (err) {
      userTz = moment.tz.guess();
    }

    return userTz;
  };

  const timezoneOptions = timezones
    .map((tz) => ({ value: tz.name, label: tz.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const columns = [
    {
      title: "Name",
      field: "name",
      searchable: true,
    },
    {
      title: "City",
      field: "city",
      searchable: true,
    },
    {
      title: "State",
      field: "state",
      searchable: true,
    },
    {
      title: "Country",
      field: "country",
      searchable: true,
    },
    {
      title: "Timezone",
      field: "timezone",
      searchable: true,
    },
    {
      title: "# Persons",
      field: "person_count",
      searchable: true,
    },
  ];

  const fields = {
    name: {
      type: "text",
      default: "",
      label: "Name",
    },
    address_one: {
      xs: 6,
      type: "text",
      default: "",
      label: "Address 1",
    },
    address_two: {
      xs: 6,
      type: "text",
      default: "",
      label: "Address 2",
    },
    city: {
      xs: 6,
      type: "text",
      default: "",
      label: "City",
    },
    state: {
      xs: 6,
      type: "text",
      default: "",
      label: "State/Province",
    },
    postal: {
      type: "text",
      default: "",
      label: "Postal Code",
    },
    phone: {
      xs: 6,
      type: "text",
      default: "",
      label: "Phone Number",
    },
    mobile: {
      xs: 6,
      type: "text",
      default: "",
      label: "Mobile/Alternate Number",
    },
    fax: {
      type: "text",
      default: "",
      label: "Fax Number",
    },
    country: {
      type: "select",
      default: "US",
      label: "Country",
      lookup: Object.entries(countries.getNames("en"))
        .map(([key, val]) => ({ value: key, label: val }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    timezone: {
      type: "select",
      label: "Timezone",
      lookup: timezoneOptions,
      default: getUserTimezone(),
    },
  };

  const layout = Object.keys(fields).map((field) => {
    return { field: field, xs: fields[field].xs ? fields[field].xs : 12 };
  });

  const handleEditTableRowClick = (rowDetails) => {
    //rowDetails, in this case, is really just the user info/model
    setSelectedModel(rowDetails);
    setOpenForm(true);
  };

  return (
    <span className="dashboard locations-page">
      <Dialog
        onClose={handleClose}
        aria-labelledby="simple-dialog-title"
        open={openForm}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle id="simple-dialog-title">
          {selectedModel.id ? `Edit ${selectedModel.name}` : "New Location"}
        </DialogTitle>
        <SimpleModelForm
          spacing={2}
          model={selectedModel}
          onClose={handleClose}
          actions={[
            {
              title: "Cancel",
              type: "button",
              action: handleClose,
            },
            {
              title: "Save",
              type: "submit",
            },
          ]}
          fields={fields}
          layout={layout}
        />
      </Dialog>

      <SimpleTable
        isNorthStar
        title="Locations"
        data={locations}
        columns={columns}
        rowClick={handleEditTableRowClick}
        isFetching={!ready}
        toolbarActions={[
          {
            icon: AddCircleIcon,
            tooltip: "Add New Location",
            onClick: (event) => {
              setSelectedModel(new LocationModel({ orgId: orgId }));
              setOpenForm(true);
            },
          },
        ]}
        actions={[
          {
            icon: EditIcon,
            tooltip: "Edit Location",
            onClick: (event, model) => {
              handleEditTableRowClick(model);
            },
          },
        ]}
      />
    </span>
  );
};

LocationsPageView.propTypes = {
  orgId: PropTypes.string.isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({})),
};

LocationsPageView.defaultProps = {
  locations: [],
};

export const LocationsComponent = LocationsPageView;

const mapStateToProps = (state) => {
  return {
    orgId: state.location.payload.orgId,
    timezones: state.session.settings.accountConstants.timezones,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    reload: (force) => dispatch(loadPageData(force)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationsPageView);
